// tosVersionesAnteriores.js
//
// Texto CONGELADO de los Términos v1.x (Tipo A y Tipo C), copiado literal del front que
// estaba en prod al hacer el bump único a v2.0 (51e60d9b, src/content/tosContent.js y
// tosContentInterno.js). NO SE EDITA: es lo que firmó cada cuenta que aceptó una v1.x.
//
// Cubre R47 — .claude/specs/1683-derechos-usuario/ ("las versiones ya aceptadas no cambian de
// contenido"). Por qué sigue en el bundle (revisor externo del bump, 20-oct-2026, A2): el
// deploy no es atómico. Si el front sube antes que el back, o el entorno de prod fija
// CURRENT_TOS_VERSION=v1.0, el gate pide una v1.x y tiene que mostrar ESTE texto, no el nuevo
// con el número viejo. Se puede borrar cuando el back exija v2.0 en prod y nadie tenga el gate
// pendiente con una v1.x (#1681).
//
// Contiene frases que la Política mínima v2.0 retira (por eso el candado de lenguaje de
// 58_internacional/09_guard_textos.py las cuenta en el bundle): solo se muestran si el
// backend pide una v1.x.

const TOS_CONTACT_EMAIL = "app.smartdx@gmail.com";

export const getTosMarkdownV1 = (version = "") => `# Términos de Uso — Plataforma SmartDx
### Modalidad individual para profesionales de la salud (Tipo A)

**Emisor:** Salud Wild SpA, RUT 78.149.483-6 ("SmartDx", "nosotros").
**Versión:** ${version || "vigente"} · **Vigencia:** desde tu aceptación.

Al marcar la casilla **"He leído y acepto los Términos de Uso"** y activar tu cuenta, aceptas
estos Términos. Si contratas en representación de tu empresa (SpA / E.I.R.L.), declaras estar
facultado para obligarla. Estos Términos, junto con la
[Política de Privacidad](https://smardx.com/privacidad.html) y —cuando exista— el Contrato SaaS suscrito
entre las partes, regulan el uso de la Plataforma SmartDx.

---

## 1. Qué es SmartDx (y qué no es)

SmartDx es una **herramienta de apoyo a la documentación clínica**, asistida con inteligencia
artificial. Te ayuda a redactar, estructurar, editar y visualizar borradores de documentación
clínica (por defecto, del **Modo Turno**: evoluciones, ingresos, pendientes, indicaciones y
resúmenes por paciente de tu ronda o turno).

**SmartDx NO es un dispositivo médico y no reemplaza tu juicio clínico.** No emite diagnósticos,
indicaciones terapéuticas, alertas, priorizaciones ni recomendaciones clínicas autónomas. Todo
lo que genera es un **borrador preliminar y editable**: tú lo revisas, corriges, validas y, en
su caso, firmas antes de usarlo, comunicarlo o incorporarlo a la ficha clínica. La decisión
clínica final es siempre tuya.

## 2. Tu responsabilidad como profesional

Al usar SmartDx declaras y aceptas que:

- Eres un profesional de la salud habilitado y usas la Plataforma para **fines profesionales y
  asistenciales**, sólo respecto de pacientes en cuya atención intervienes directamente como
  profesional tratante.
- **Toda** evaluación, diagnóstico, indicación, tratamiento, decisión clínica, revisión,
  validación, firma e incorporación a la ficha clínica de cualquier borrador generado con
  SmartDx es de tu **exclusiva responsabilidad** profesional, clínica, ética y legal.
- Cuentas con las autorizaciones, permisos y bases legales necesarias para tratar en la
  Plataforma la información y los datos de tus pacientes (incluidos datos sensibles de salud), y
  con los perfiles de acceso institucionales cuando uses información bajo custodia de una
  clínica, hospital u otra institución.
- La custodia, conservación e incorporación a la ficha clínica de los documentos definitivos es
  tuya (o de la institución que corresponda). **SmartDx no es repositorio ni custodio de fichas
  clínicas.**

## 3. Datos personales y privacidad

- El tratamiento de datos se realiza **al estándar de la Ley N° 21.719** (protección de datos
  personales), rigiendo hoy la Ley N° 19.628 en lo que corresponda.
- **Tú eres el Responsable** del tratamiento de los datos de tus pacientes (decides qué
  ingresas y para qué). **SmartDx (Salud Wild SpA) actúa como Encargado**, tratando esos datos
  únicamente por tu cuenta y según tus instrucciones, para prestarte el servicio.
- Para operar, SmartDx usa proveedores tecnológicos (subencargados) que pueden estar fuera de
  Chile —entre otros AWS, OpenAI, Anthropic, Deepgram, Cloudflare y Sentry—, sujetos a
  resguardos de confidencialidad y seguridad. El detalle, las transferencias internacionales,
  los plazos de retención y el ejercicio de tus derechos (acceso, rectificación, cancelación,
  oposición y portabilidad) se describen en la
  [Política de Privacidad](https://smardx.com/privacidad.html).
- Aplicamos medidas técnicas y organizativas razonables (incluida seudonimización antes del
  procesamiento por IA) y te informaremos, **sin dilaciones indebidas y por los medios más
  expeditos posibles**, cualquier incidente de seguridad relevante que afecte tus datos.
- Al término del servicio, tus datos serán devueltos, eliminados o anonimizados de forma
  irreversible, según corresponda.

## 4. Licencia de uso

Te otorgamos una licencia **limitada, temporal, no exclusiva, intransferible, no sublicenciable
y revocable** para usar la Plataforma durante la vigencia del servicio y sólo para tus fines
profesionales. La propiedad intelectual de SmartDx (software, modelos, interfaces, marca) es y
seguirá siendo de Salud Wild SpA. Los **outputs** que generes a partir de tu información son
tuyos (o de tu institución).

Te comprometes a **no** compartir tus credenciales (son personales e intransferibles), ni hacer
ingeniería reversa, copia, extracción masiva, scraping, reventa o cualquier uso no autorizado de
la Plataforma, y a avisarnos ante cualquier falla, acceso no autorizado o incidente que
detectes.

## 5. Precio

El precio, la modalidad de pago y las condiciones económicas del servicio se rigen por el
**Contrato SaaS** suscrito entre las partes (o por la orden de contratación aceptada dentro de
la app, cuando corresponda). La falta de pago faculta a SmartDx a suspender el acceso.

## 6. Disponibilidad y responsabilidad

- Hacemos esfuerzos comercialmente razonables por mantener la Plataforma disponible, pero puede
  haber interrupciones (mantenciones, fallas de terceros, fuerza mayor). No garantizamos
  operación ininterrumpida ni libre de errores.
- SmartDx **no responde** por decisiones clínicas ni por errores derivados de la información que
  tú o terceros ingresen. No respondemos por daños indirectos, lucro cesante ni perjuicios
  consecuenciales.
- Nuestra responsabilidad total acumulada no excederá las sumas que nos hayas pagado en los seis
  (6) meses anteriores al hecho que origine el reclamo.

## 7. Vigencia y término

Estos Términos rigen mientras uses la Plataforma. Cualquiera de las partes puede terminar el
servicio conforme al Contrato SaaS aplicable (por defecto: período piloto acordado, renovable
por períodos mensuales; no renovación con aviso de 15 días corridos; término inmediato por
incumplimiento grave no subsanado). El término no libera de pagos ya devengados.

## 8. Ley aplicable y jurisdicción

Estos Términos se rigen por la ley chilena. Cualquier controversia se somete a los Tribunales
Ordinarios de Justicia de Santiago de Chile.

## 9. Aceptación

Al marcar **"He leído y acepto los Términos de Uso"** manifiestas tu consentimiento mediante
firma electrónica simple (Ley N° 19.799). Dejaremos registro de tu identidad de usuario, fecha y
hora de aceptación y versión aceptada de estos Términos.

Contacto: ${TOS_CONTACT_EMAIL} · Salud Wild SpA · Antonio Bellet 193, of. 1210, Providencia,
Santiago.

---

> **Recuerda:** SmartDx es un asistente de redacción clínica. Los borradores son preliminares y
> editables, y **no reemplazan tu juicio clínico** como profesional tratante.
`;

export const getTosInternoMarkdownV1 = (
  version = ""
) => `# Términos de Uso — Plataforma SmartDx
### Modalidad interno/a de medicina en formación (Tipo C)

**Emisor:** Salud Wild SpA, RUT 78.149.483-6 ("SmartDx", "nosotros").
**Versión:** ${version || "vigente"} · **Vigencia:** desde tu aceptación.

Al marcar la casilla **"He leído y acepto los Términos de Uso"** y activar tu cuenta, aceptas
estos Términos. Estos Términos y la [Política de Privacidad](https://smardx.com/privacidad.html) regulan tu
uso de la Plataforma SmartDx como interno/a de medicina.

---

## 1. Qué es SmartDx (y qué no es)

SmartDx es una **herramienta de apoyo a la documentación clínica**, asistida con inteligencia
artificial. Te ayuda a redactar, estructurar, editar y visualizar borradores de documentación
clínica (por defecto, del **Modo Turno**: evoluciones, ingresos, pendientes, indicaciones y
resúmenes por paciente de tu ronda o turno).

**SmartDx NO es un dispositivo médico y no reemplaza tu juicio clínico.** No emite diagnósticos,
indicaciones terapéuticas, alertas, priorizaciones ni recomendaciones clínicas autónomas. Todo
lo que genera es un **borrador preliminar y editable**: tú lo revisas, corriges, validas y, en
su caso, firmas antes de usarlo, comunicarlo o incorporarlo a la ficha clínica. La decisión
clínica final es siempre tuya.

## 2. Tu responsabilidad como interno/a de medicina

Al usar SmartDx declaras y aceptas que:

- Eres **estudiante de medicina cursando el internado** en una institución de salud, y usas la
  Plataforma **exclusivamente** en el marco de tu práctica clínica asignada, respecto de
  pacientes a cuya atención concurres y a cuya información accedes de forma autorizada.
- **No declaras ser un profesional habilitado ni el médico tratante.** Firmas los documentos con
  tu cargo de interno/a ("Int." seguido de tu apellido) y no con el de un médico titulado.
- **Asumes la responsabilidad del contenido que redactas, revisas y firmas.** Toda revisión,
  validación, corrección y firma de cualquier borrador generado con SmartDx, así como su
  exactitud y su uso o incorporación a la ficha clínica, son de **tu responsabilidad**
  académica, ética y —en lo que corresponda a tu calidad de interno/a— legal. El hecho de que
  SmartDx haya generado un borrador no traslada esa responsabilidad a SmartDx.
- Reconoces que, por tu condición de interno/a, los documentos que produzcas quedan sujetos a la
  **supervisión, validación y contrafirma** que exija tu centro formador o asistencial. Cumplir
  con esas reglas es tu obligación; **no las reemplaza ni las suple el uso de SmartDx**.
- Cuentas con los **perfiles de acceso institucionales** y las autorizaciones necesarias para
  acceder y tratar la información de los pacientes con la que trabajas en la Plataforma
  (incluidos datos sensibles de salud), y usarás únicamente la información a la que tu
  institución te haya dado acceso legítimo.
- La custodia, conservación e incorporación a la ficha clínica de los documentos definitivos es
  de tu institución (o de quien corresponda). **SmartDx no es repositorio ni custodio de fichas
  clínicas.**

## 3. Datos personales y privacidad

- El tratamiento de datos se realiza **al estándar de la Ley N° 21.719** (protección de datos
  personales), rigiendo hoy la Ley N° 19.628 en lo que corresponda.
- **Tú decides qué información ingresas a la Plataforma y para qué**, y respondes por ese
  ingreso. **SmartDx (Salud Wild SpA) actúa como Encargado**, tratando esos datos únicamente por
  tu cuenta y según tus instrucciones, para prestarte el servicio.
- Declaras que actúas **dentro de los permisos y las instrucciones de la institución** en la que
  realizas tu internado. Cuando esa institución sea el Responsable del tratamiento de los datos
  de sus pacientes, declaras contar con su autorización para usar la Plataforma con esa
  información y te comprometes a suspender su uso si dicha autorización cesa.
- Para operar, SmartDx usa proveedores tecnológicos (subencargados) que pueden estar fuera de
  Chile —entre otros AWS, OpenAI, Anthropic, Deepgram, Cloudflare y Sentry—, sujetos a
  resguardos de confidencialidad y seguridad. El detalle, las transferencias internacionales,
  los plazos de retención y el ejercicio de tus derechos (acceso, rectificación, cancelación,
  oposición y portabilidad) se describen en la
  [Política de Privacidad](https://smardx.com/privacidad.html).
- Aplicamos medidas técnicas y organizativas razonables (incluida seudonimización antes del
  procesamiento por IA) y te informaremos, **sin dilaciones indebidas y por los medios más
  expeditos posibles**, cualquier incidente de seguridad relevante que afecte tus datos.
- Al término del servicio, tus datos serán devueltos, eliminados o anonimizados de forma
  irreversible, según corresponda.

## 4. Licencia de uso

Te otorgamos una licencia **limitada, temporal, no exclusiva, intransferible, no sublicenciable
y revocable** para usar la Plataforma durante la vigencia del acceso y sólo para tu práctica
clínica formativa. La propiedad intelectual de SmartDx (software, modelos, interfaces, marca) es
y seguirá siendo de Salud Wild SpA. Los **outputs** que generes a partir de tu información son
tuyos (o de tu institución).

Te comprometes a **no** compartir tus credenciales (son personales e intransferibles), ni hacer
ingeniería reversa, copia, extracción masiva, scraping, reventa o cualquier uso no autorizado de
la Plataforma, y a avisarnos ante cualquier falla, acceso no autorizado o incidente que
detectes.

## 5. Período de prueba gratuito y eventual paso a modalidad pagada

Tu acceso se otorga en modalidad de formación y **sin contrato de servicios asociado**, sin
obligación de pago alguna.

Al crear tu cuenta puedes usar la Plataforma de inmediato, sin trámites previos. El plazo de tu
prueba gratuita (**14 días**) comienza a correr con el **primer documento clínico que generes**
con SmartDx (tu primera evolución, ingreso o resumen). Si no generas ningún documento, la prueba
igual comienza a los **30 días** de creada tu cuenta. Subir tu carnet de identidad **no alarga**
este plazo: es un trámite aparte, para que la revisión humana de tu cuenta pueda completarse.

Además te vamos a pedir tu carnet de identidad, para que la revisión humana de tu cuenta pueda
completarse. Es un trámite aparte: no tiene plazo propio y no subirlo no suspende tu acceso
durante la prueba.

Durante toda la prueba no tienes obligación de pago alguna, ni SmartDx asume compromiso de
permanencia, disponibilidad o soporte.

**Al vencer el plazo vigente, el acceso puede terminar.** Si ambas partes quieren continuar,
SmartDx podrá ofrecerte seguir usando la Plataforma en **modalidad pagada**, cuyo precio y
condiciones se regirán por el Contrato SaaS o la orden de contratación que aceptes en ese
momento, y por los Términos que correspondan a esa modalidad.

**No hay renovación automática ni cobro sin tu consentimiento expreso**: no se te cobrará nada
por el uso de la Plataforma mientras no aceptes de forma explícita las condiciones económicas de
la modalidad pagada.

Podemos **limitar, suspender o revocar** el acceso en cualquier momento —también antes del
vencimiento del plazo vigente—, en particular si termina tu internado, si cesa la autorización de
tu institución o si haces un uso contrario a estos Términos.

## 6. Disponibilidad y responsabilidad

- Hacemos esfuerzos comercialmente razonables por mantener la Plataforma disponible, pero puede
  haber interrupciones (mantenciones, fallas de terceros, fuerza mayor). No garantizamos
  operación ininterrumpida ni libre de errores.
- SmartDx **no responde** por decisiones clínicas ni por errores derivados de la información que
  tú o terceros ingresen. No respondemos por daños indirectos, lucro cesante ni perjuicios
  consecuenciales.
- Tratándose de un acceso sin costo, **nuestra responsabilidad total acumulada se limita al
  máximo permitido por la ley aplicable**.

## 7. Vigencia y término

Estos Términos rigen mientras uses la Plataforma. El acceso termina **al vencer tu prueba
gratuita** —salvo que acuerdes continuar en modalidad pagada— y, en todo caso, al concluir tu
internado o la autorización institucional que lo sustenta. Cualquiera de las partes puede
ponerle término en cualquier momento, sin expresión de causa y sin indemnización alguna.

## 8. Ley aplicable y jurisdicción

Estos Términos se rigen por la ley chilena. Cualquier controversia se somete a los Tribunales
Ordinarios de Justicia de Santiago de Chile.

## 9. Aceptación

Al marcar **"He leído y acepto los Términos de Uso"** manifiestas tu consentimiento mediante
firma electrónica simple (Ley N° 19.799). Dejaremos registro de tu identidad de usuario, fecha y
hora de aceptación y versión aceptada de estos Términos.

Contacto: ${TOS_CONTACT_EMAIL} · Salud Wild SpA · Antonio Bellet 193, of. 1210, Providencia,
Santiago.

---

> **Recuerda:** SmartDx es un asistente de redacción clínica. Los borradores son preliminares y
> editables, y **no reemplazan tu juicio clínico**. Firmas como **Int. \\<Apellido\\>** y respondes
> por lo que firmas, sin perjuicio de la supervisión que exija tu centro.
`;
